import { motion } from "framer-motion";
import { Scissors, Sparkles, Paintbrush, Crown, Quote } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const categories = [
  { icon: Scissors, name: "Hair", desc: "Cuts, color, keratin & spa rituals" },
  { icon: Sparkles, name: "Skin", desc: "Facials, cleanups & glow care" },
  { icon: Paintbrush, name: "Makeup", desc: "HD bridal & party makeovers" },
  { icon: Crown, name: "Bridal & Party", desc: "Complete occasion packages" },
];

export const OurSalon = () => (
  <section className="py-20 lg:py-28 bg-[#FAFAF8]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <motion.div {...fadeUp} className="text-center mb-14">
        <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs font-semibold mb-3">What We Do</p>
        <h2 className="font-serif-brand text-4xl sm:text-5xl text-[#1C1C1C] font-medium tracking-tight">Our Salon</h2>
      </motion.div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="salon-categories">
        {categories.map((c, i) => (
          <motion.a key={c.name} href="#services" {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`salon-category-${c.name.toLowerCase().replace(/ & /g, "-")}`}
            className="group bg-white border border-[#E5E7E5] rounded-md p-8 text-center shadow-[0_4px_20px_rgba(10,63,47,0.05)] hover:border-[#0A3F2F]/40 transition-colors">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F5EDE3] text-[#0A3F2F] flex items-center justify-center mb-5 group-hover:bg-[#0A3F2F] group-hover:text-[#C5A059] transition-colors">
              <c.icon className="w-6 h-6" />
            </div>
            <h3 className="font-serif-brand text-2xl font-semibold text-[#1C1C1C]">{c.name}</h3>
            <p className="text-[#5C6A63] text-sm mt-2 font-light">{c.desc}</p>
            <p className="text-[#C5A059] text-xs uppercase tracking-wider font-bold mt-4">Learn More →</p>
          </motion.a>
        ))}
      </div>
    </div>
  </section>
);

export const PackagesSection = ({ packages }) => (
  <section id="packages" className="py-20 lg:py-28 bg-[#F5EDE3]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <motion.div {...fadeUp} className="text-center mb-14">
        <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs font-semibold mb-3">Best Value</p>
        <h2 className="font-serif-brand text-4xl sm:text-5xl text-[#1C1C1C] font-medium tracking-tight">Packages</h2>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-6" data-testid="packages-list">
        {packages.map((p, i) => (
          <motion.div key={p.id} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`package-card-${p.id}`}
            className="bg-white rounded-md p-8 shadow-[0_4px_20px_rgba(10,63,47,0.08)] flex flex-col">
            <h3 className="font-serif-brand text-2xl font-semibold text-[#0A3F2F]">{p.name}</h3>
            <p className="text-[#5C6A63] text-sm mt-3 font-light leading-relaxed flex-1">{p.description}</p>
            <div className="flex items-baseline justify-between mt-6 pt-5 border-t border-[#E5E7E5]">
              <span className="text-2xl font-bold text-[#0A3F2F]">₹{p.price.toLocaleString("en-IN")}</span>
              <a href="#book" data-testid={`package-book-${p.id}`}
                className="btn-lift bg-[#C5A059] text-[#1C1C1C] text-xs uppercase tracking-wide font-bold px-5 py-2.5 rounded-full hover:bg-[#d4b06c]">
                Book Now
              </a>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const testimonials = [
  { quote: "Best salon in Sonipat! My keratin treatment turned out amazing — hair feels silky even after months.", name: "Neha S." },
  { quote: "The bridal makeup made my wedding day perfect. High-end products and such a professional team.", name: "Priyanka M." },
  { quote: "Joined the academy's hair course — real hands-on training with actual clients. Best decision of my career.", name: "Ritu K." },
];

export const Testimonials = () => (
  <section className="py-20 lg:py-28 bg-[#FAFAF8]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <motion.div {...fadeUp} className="text-center mb-14">
        <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs font-semibold mb-3">Loved by Sonipat</p>
        <h2 className="font-serif-brand text-4xl sm:text-5xl text-[#1C1C1C] font-medium tracking-tight">Client Testimonials</h2>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials-list">
        {testimonials.map((t, i) => (
          <motion.div key={t.name} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
            className="bg-white border border-[#E5E7E5] rounded-md p-8 shadow-[0_4px_20px_rgba(10,63,47,0.05)]">
            <Quote className="w-7 h-7 text-[#C5A059] mb-4" />
            <p className="text-[#1C1C1C] text-sm leading-relaxed font-light italic">"{t.quote}"</p>
            <p className="text-[#0A3F2F] font-bold text-sm mt-5">— {t.name}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
